<script setup lang="ts">
import { computed } from 'vue'
import type { Quote, QuoteItem } from '@/services/quotes'
import { formatCurrency, formatShortDate } from '@/utils/format'
import { statusLabel } from '@/utils/status'

/**
 * El documento que se imprime.
 *
 * Existe aparte de la ficha porque son dos cosas distintas: la ficha es una pantalla de
 * aplicación —con Volver, Facturar, Borrar y el panel de seguimiento con sus notas— y el
 * documento es lo que se le manda al cliente. Imprimir la ficha tal cual daría un PDF
 * con botones y notas internas, que no se le manda a nadie.
 *
 * Aquí van los datos y nada más: **sin notas de seguimiento**. Son el hilo interno del
 * comercial con su cliente sobre esta cotización —"llamó, no contesta", "va a mirar el
 * precio"— y son exactamente lo que no debe salir del despacho.
 *
 * ## Por qué los colores están escritos a mano
 *
 * Ni `surface-*` ni `dark:`. Los colores de la aplicación son variables, y las variables
 * se voltean solas con el tema del sistema: con la pantalla en modo oscuro, una cabecera
 * gris sobre fondo blanco se imprimiría negra, y el documento saldría con el texto
 * blanco sobre un folio negro. Media hoja de tinta gastada y un documento ilegible.
 *
 * Un impreso no tiene tema. Por eso aquí no hay tokens: hay colores fijos, los mismos
 * en un monitor que en una impresora.
 */

const props = defineProps<{ quote: Quote }>()

/** Sets con componentes, que necesitan el aviso de que el desglose no suma aparte. */
const haySets = computed(() => props.quote.items.some((item) => item.is_set))

/** Las líneas del documento: las del primer nivel y, debajo, sus componentes. */
const filas = computed<Array<{ item: QuoteItem; hijo: boolean }>>(() =>
  props.quote.items.flatMap((item) => [
    { item, hijo: false },
    ...item.children.map((componente) => ({ item: componente, hijo: true })),
  ]),
)

/**
 * La cantidad tal y como la escribió el comercial.
 *
 * Un `InputNumber` con `es-CO` devuelve `"1"` o `"1.5"`, pero un cálculo de la aplicación
 * puede devolver `"1.0000"`. En un impreso eso son cuatro ceros de más en cada línea, y
 * multiplicados por treinta artículos.
 */
function cantidad(value: string): string {
  const numero = Number.parseFloat(value)

  return Number.isFinite(numero) ? String(numero) : value
}
</script>

<template>
  <article class="quote-print">
    <!-- Cabecera: qué es esto, de quién es y cuándo -->
    <header class="quote-print__cabecera">
      <div>
        <h1 class="quote-print__titulo">
          COTIZACIÓN
        </h1>
        <dl class="quote-print__datos">
          <div>
            <dt>Nº Cot.</dt>
            <dd>{{ quote.quote_number ?? '—' }}</dd>
          </div>
          <div>
            <dt>Fecha</dt>
            <dd>{{ formatShortDate(quote.quote_date) }}</dd>
          </div>
          <div>
            <dt>Estado logístico</dt>
            <dd>{{ statusLabel(quote.status) }}</dd>
          </div>
          <div>
            <dt>Tipo</dt>
            <dd>{{ quote.price_type === 'wholesale' ? 'Venta al mayoreo' : 'Venta al detal' }}</dd>
          </div>
          <div v-if="quote.user_name">
            <dt>Asesor</dt>
            <dd>{{ quote.user_name }}</dd>
          </div>
          <!--
            La línea va junto al asesor porque es a qué línea entra la venta, y el
            impreso es el documento que se archiva: si no está aquí, la línea se
            pierde en cuanto el papel sale del sistema. Con `v-if` porque en el
            impreso solo van los datos que tienen algo que decir.
          -->
          <div v-if="quote.attention_line">
            <dt>Línea de atención</dt>
            <dd>{{ quote.attention_line }}</dd>
          </div>
          <!--
            Anticipos: solo cuando hay, porque el total de arriba ya dice lo que se
            va a cobrar. Un bloque con ceros en un impreso que se manda al cliente es
            ruido, y el valor restante es lo único que aporta: es la pregunta que se
            hace quien recibe el papel.
          -->
          <div
            v-if="quote.has_advances"
            class="avances"
          >
            <dt>Anticipos</dt>
            <dd>{{ formatCurrency(quote.advances_total) }}</dd>
            <dt>Valor restante</dt>
            <dd>{{ formatCurrency(quote.balance) }}</dd>
          </div>
          <div v-if="quote.is_invoiced && quote.invoice_number">
            <dt>Factura</dt>
            <dd>{{ quote.invoice_number }}</dd>
          </div>
        </dl>
      </div>

      <!--
        A la derecha, como en el impreso que facilita el cliente: los datos de contacto
        se leen de un vistazo en el margen, sin buscarlos.
      -->
      <div class="quote-print__cliente">
        <h2>CLIENTE</h2>
        <p class="quote-print__cliente-nombre">
          {{ quote.customer_name }}
        </p>
        <p v-if="quote.document">
          <span>Doc:</span> {{ quote.document }}
        </p>
        <p v-if="quote.customer_email">
          <span>{{ quote.customer_email }}</span>
        </p>
        <p v-if="quote.customer_phone">
          <span>{{ quote.customer_phone }}</span>
        </p>
      </div>
    </header>

    <!-- Los artículos -->
    <table class="quote-print__tabla">
      <thead>
        <tr>
          <th scope="col">
            Descripción
          </th>
          <th
            scope="col"
            class="quote-print__num"
          >
            Cant.
          </th>
          <th
            scope="col"
            class="quote-print__num"
          >
            Precio unit.
          </th>
          <th
            scope="col"
            class="quote-print__num"
          >
            Subtotal
          </th>
        </tr>
      </thead>

      <tbody>
        <tr
          v-for="fila in filas"
          :key="`${fila.item.id}-${fila.hijo}`"
          :class="{ 'quote-print__componente': fila.hijo }"
        >
          <td>
            <span
              v-if="fila.item.reference"
              class="quote-print__referencia"
            >{{ fila.item.reference }}</span>
            {{ fila.item.name }}
            <!--
              La cantidad del componente va dentro de la descripción y sus columnas se
              quedan vacías. Es lo que evita el error de leer el desglose como una
              segunda factura: si el componente tuviera precio unitario y subtotal al
              lado, el total de abajo no saldría.
            -->
            <span
              v-if="fila.hijo"
              class="quote-print__veces"
            > × {{ cantidad(fila.item.quantity) }}</span>
          </td>
          <td class="quote-print__num">
            <template v-if="!fila.hijo">
              {{ cantidad(fila.item.quantity) }}
            </template>
          </td>
          <td class="quote-print__num">
            <template v-if="!fila.hijo">
              {{ formatCurrency(fila.item.price) }}
            </template>
          </td>
          <td class="quote-print__num">
            <template v-if="!fila.hijo">
              {{ formatCurrency(fila.item.line_total) }}
            </template>
          </td>
        </tr>

        <tr
          v-if="quote.items.length === 0"
          class="quote-print__vacia"
        >
          <td colspan="4">
            Esta cotización no tiene artículos.
          </td>
        </tr>
      </tbody>

      <tfoot>
        <tr>
          <th
            scope="row"
            colspan="3"
          >
            TOTAL
          </th>
          <td class="quote-print__num quote-print__total">
            {{ formatCurrency(quote.total_price) }}
          </td>
        </tr>
      </tfoot>
    </table>

    <!--
      El aviso del desglose. Sin él, un cliente que lea «Camisa 4 × $10.000» debajo de un
      set entiende que se le está cobrando dos veces, y llama a preguntar. Con una
      línea fuera, no.
    -->
    <p
      v-if="haySets"
      class="quote-print__pie"
    >
      Los componentes de los sets van desglosados solo como detalle: no se cobran aparte,
      el set se cobra por su precio.
    </p>

    <!--
      Las dos líneas de firma. Con hueco de verdad encima, porque si la línea va pegada
      al nombre no hay sitio donde firmar y quien tiene que firmar termina firmando en el
      margen.
    -->
    <footer class="quote-print__firmas">
      <div>
        <span class="quote-print__linea" />
        <p>Firma Autorizada</p>
      </div>
      <div>
        <span class="quote-print__linea" />
        <p>Aceptado por Cliente</p>
      </div>
    </footer>
  </article>
</template>

<style scoped>
/*
 * Los colores están escritos uno a uno y a propósito. Está explicado arriba: un impreso
 * no hereda el tema de la pantalla, y con estos tokens salía un folio negro si quien
 * imprimía tenía el sistema en modo oscuro.
 */
.quote-print {
  --linea: #d4d4d8;
  --gris: #71717a;
  --tinta: #18181b;

  color: var(--tinta);

  /*
   El papel lo trae el documento, y no lo hereda de la página.

   Escribir `background: #fff` en `html, body` dentro de `@media print` no basta: si el
   navegador no aplica ese bloque —porque va con una hoja antigua en caché, o porque
   la vista previa se hizo con la regla de medios desactivada— el fondo sale del tema y
   este texto casi negro cae sobre un folio oscuro. Documento ilegible.

   Poner el papel aquí, en el propio documento, hace que el resultado no dependa de
   que la hoja aplique. Y con `color-scheme: light` el navegador deja de intentar
   invertir nada, incluidos los controles y las barras de desplazamiento.
  */
  background: #ffffff;
  color-scheme: light;

  /*
   El color de fondo se imprime solo a petición.

   El navegador lo quita por defecto —un fondo que nadie pidió sale en todas las
   impresoras—, y entonces la cabecera de la tabla saldría sin su franja gris, que es
   justo lo que separa el título de las columnas del resto de la tabla.
  */
  print-color-adjust: exact;
  -webkit-print-color-adjust: exact;
  font-family:
    ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  font-size: 11pt;
  line-height: 1.4;
  text-align: left;
}

/* ── Cabecera ── */
.quote-print__cabecera {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 12pt;
  padding-bottom: 8pt;
  border-bottom: 2px solid var(--linea);
}

.quote-print__titulo {
  margin: 0;
  font-size: 18pt;
  font-weight: 700;
  letter-spacing: 0.06em;
}

.quote-print__datos {
  margin: 8pt 0 0;
  display: grid;
  gap: 2pt;
  font-size: 9.5pt;
}

.quote-print__datos > div {
  display: flex;
  gap: 6pt;
}

.quote-print__datos dt {
  color: var(--gris);
}

.quote-print__datos dd {
  margin: 0;
  font-weight: 600;
}

.quote-print__cliente {
  min-width: 45%;
  font-size: 9.5pt;
}

.quote-print__cliente h2 {
  margin: 0;
  font-size: 10pt;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-align: right;
}

.quote-print__cliente p {
  margin: 1pt 0;
  text-align: right;
}

.quote-print__cliente span {
  color: var(--gris);
}

.quote-print__cliente-nombre {
  font-size: 12pt;
  font-weight: 600;
}

/* ── Tabla ── */
.quote-print__tabla {
  width: 100%;
  margin-top: 10pt;
  border-collapse: collapse;
  /* Sin tramas alternas: en un impreso de treinta líneas salen como manchas de gris. */
}

.quote-print__tabla th,
.quote-print__tabla td {
  padding: 4pt 6pt;
  border-bottom: 1px solid var(--linea);
  vertical-align: top;
}

.quote-print__tabla thead th {
  border-bottom: 1.5px solid var(--tinta);
  font-size: 9pt;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-align: left;
  text-transform: uppercase;
}

.quote-print__num {
  text-align: right;
  white-space: nowrap;
}

.quote-print__referencia {
  color: var(--gris);
  font-family: ui-monospace, 'SFMono-Regular', Menlo, Consolas, monospace;
  font-size: 9pt;
}

.quote-print__veces {
  color: var(--gris);
  font-size: 9.5pt;
}

/*
 * Un componente se sangra y se ve más pequeño, y no tiene borde propio: el desglose es
 * parte de la línea de su set, no una fila más de la tabla.
 */
.quote-print__componente td {
  border-bottom: none;
  color: var(--gris);
  font-size: 9.5pt;
  padding-left: 16pt;
}

.quote-print__vacia td {
  padding-top: 10pt;
  text-align: center;
}

.quote-print__total {
  font-size: 12pt;
  font-weight: 700;
}

.quote-print__tabla tfoot th,
.quote-print__tabla tfoot td {
  border-top: 2px solid var(--tinta);
  border-bottom: none;
  padding-top: 6pt;
}

.quote-print__tabla tfoot th {
  font-size: 10pt;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-align: right;
}

/* ── Pie ── */
.quote-print__pie {
  margin: 8pt 0 0;
  color: var(--gris);
  font-size: 8.5pt;
  font-style: italic;
}

.quote-print__firmas {
  display: flex;
  gap: 24pt;
  justify-content: space-between;
  /* El hueco para firmar: alto suficiente para una firma a mano, con la línea debajo. */
  margin-top: 56pt;
}

.quote-print__firmas > div {
  flex: 1;
}

.quote-print__linea {
  display: block;
  border-top: 1px solid var(--tinta);
}

.quote-print__firmas p {
  margin: 2pt 0 0;
  font-size: 9pt;
  text-align: center;
}
</style>